import { ref, watch, onBeforeMount } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useCluberLocalStorage } from '@cluber/tools-composables'
import { useGetLegacyPeopleGroupByClub } from '@dashboard/modules/ProfileSelector/composables'
import useGetClubLocale from '@libs/fsc/composables/useGetClubLocale/useGetClubLocale'
import { useApiLegacyRequest } from '@services/Legacy/request'
import { $resetPiniaStores } from '@services/api/requestStoreUtils'
import { type LocalStorageAuth } from '@services/Auth/declarations'
import { useRefreshToken } from '@services/api/useRefreshToken/useRefreshToken'
import { getAuthWorker, destroyAuthWorker } from '@core/workers/auth/authWorkerClient'
import { handleWorkerUserSessionLogout } from '@core/workers/auth/authWorkerHandlers'
import { authRedirectRules } from '@core/router/authRedirectRules'

const { storage } = useCluberLocalStorage<LocalStorageAuth>({
  auth: {
    token: '',
    userIdentifier: '',
    refreshTokenValidity: undefined,
    tokenTtl: undefined,
  },
  currentProfile: undefined,
  keepLoggedIn: false,
}, {
  initOnMounted: true,
  mergeDefaults: true,
})

export function useUserLogout() {
  const { hideModal } = useRefreshToken()
  const router = useRouter()
  const route = useRoute()
  const { setLocale } = useGetClubLocale()
  const { logoutCleanup } = useGetLegacyPeopleGroupByClub()
  const { fetchGetUserLegacyLogout } = useApiLegacyRequest()

  // ***
  // HANDLE USER SESSION LOGOUT
  // ***

  function handleUserSessionLogout() {
    $resetPiniaStores().all()
    logoutCleanup()
    storage.value = null
    window.localStorage.clear()
    localStorage.clear()

    // REQUEST TO DELETE THE LEGACY COOKIE ON LOGOUT
    fetchGetUserLegacyLogout()
    // RESET TO DEFAULT LOCALE
    setLocale({ mode: 'logout' })
    hideModal()

    // DESTROY THE CLIENT-SIDE SHARED WORKER INSTANCE
    destroyAuthWorker()

    // REDIRECT TO LOGIN
    const { isROOT, notAllowedRoutes } = authRedirectRules()

    let fromRoute

    if (!isROOT || !notAllowedRoutes) {
      fromRoute = route.query.from || window.location.pathname
    } else {
      fromRoute = undefined
    }

    router.push({
      name: 'authLogin',
      query: {
        ...((route.query?.keepCurrentUrl && fromRoute) && { from: fromRoute }),
        ...(route.query?.expiredToken && { expiredToken: 'true' }),
      },
    })
  }

  // ***
  // USER SESSION LOGOUT WITH SHARED WORKER
  // ***

  function emitWorkerSessionLogoutMessage() {
    getAuthWorker()?.postMessage({ type: 'userSessionLogout' })
  }

  // ***
  // FORCE USER SESSION LOGOUT WITH SHARED WORKER
  // ***

  function forceWorkerSessionLogout() {
    const forceLogout = ref(false)

    watch(forceLogout, (current) => {
      if (current) {
        handleUserSessionLogout()
        forceLogout.value = false
      }
    })

    onBeforeMount(() => {
      handleWorkerUserSessionLogout(() => {
        forceLogout.value = true
      })
    })
  }

  return {
    handleUserSessionLogout,
    emitWorkerSessionLogoutMessage,
    forceWorkerSessionLogout,
  }
}
